import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const MOBILE_QUERY = '(max-width: 768px)';
const OVERLAY_QUERY = '(max-width: 575.98px)';
// Fixed pixel thresholds (not a % of scrollHeight): the filter bar's own height affects
// scrollHeight when it collapses/expands, so a percentage-based threshold creates a
// feedback loop (hide -> page shrinks -> ratio drops back below threshold -> show -> repeat),
// which flickers. Two thresholds (hide higher than show) add hysteresis so scrolling near
// the boundary doesn't rapidly toggle it either.
const SCROLL_HIDE_PX = 250;
const SCROLL_SHOW_PX = 120;

function matches(query: string): boolean {
  return typeof window !== 'undefined' && window.matchMedia(query).matches;
}

function isMobileViewport(): boolean {
  return matches(MOBILE_QUERY);
}

export interface UiContextValue {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  filterBarVisible: boolean;
  filterBarPinned: boolean;
  toggleFilterBar: () => void;
}

const UiContext = createContext<UiContextValue | undefined>(undefined);

export function UiProvider({ children }: { children: ReactNode }) {
  // Starts collapsed on mobile so the sidebar never renders expanded/overlapping content
  // on first load; the user can still expand it afterwards via the toggle button.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobileViewport);
  // filterBarVisible reflects user intent (shown/hidden via the toggle); filterBarPinned
  // means the user explicitly opened it via the toggle, so it stays visible regardless of
  // scroll position; scrolledPastThreshold auto-hides the unpinned bar once scrolled down
  // past SCROLL_HIDE_PX.
  const [filterBarVisible, setFilterBarVisible] = useState(true);
  const [filterBarPinned, setFilterBarPinned] = useState(false);
  const [scrolledPastThreshold, setScrolledPastThreshold] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setSidebarCollapsed(true);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolledPastThreshold((wasPast) => {
        if (wasPast) return window.scrollY > SCROLL_SHOW_PX;
        return window.scrollY > SCROLL_HIDE_PX;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleSidebar = useCallback(() => setSidebarCollapsed((v) => !v), []);

  const effectiveFilterBarVisible =
    filterBarVisible && (!scrolledPastThreshold || filterBarPinned);

  const toggleFilterBar = useCallback(() => {
    if (effectiveFilterBarVisible) {
      setFilterBarVisible(false);
      setFilterBarPinned(false);
    } else {
      setFilterBarVisible(true);
      setFilterBarPinned(true);
    }
  }, [effectiveFilterBarVisible]);

  // Below 575.98px the expanded sidebar renders as a full-screen overlay; lock scrolling
  // on the page behind it (both axes) while it's open, and re-evaluate on resize since the
  // overlay breakpoint can be crossed while the sidebar is already expanded.
  useEffect(() => {
    if (sidebarCollapsed) return;

    const html = document.documentElement;
    const body = document.body;

    const applyLock = () => {
      const locked = matches(OVERLAY_QUERY);
      html.classList.toggle('sidebar-scroll-lock', locked);
      body.classList.toggle('sidebar-scroll-lock', locked);
    };

    applyLock();
    window.addEventListener('resize', applyLock);
    return () => {
      window.removeEventListener('resize', applyLock);
      html.classList.remove('sidebar-scroll-lock');
      body.classList.remove('sidebar-scroll-lock');
    };
  }, [sidebarCollapsed]);

  const value = useMemo(
    () => ({
      sidebarCollapsed,
      toggleSidebar,
      filterBarVisible: effectiveFilterBarVisible,
      filterBarPinned,
      toggleFilterBar,
    }),
    [sidebarCollapsed, toggleSidebar, effectiveFilterBarVisible, filterBarPinned, toggleFilterBar],
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

export function useUi(): UiContextValue {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi must be used within UiProvider');
  return ctx;
}
